
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Product } from '@/lib/supabase/products/types';
import { Image, Loader2, AlertTriangle } from "lucide-react";

interface ProductListProps {
  loading: boolean;
  error?: string | null;
  filteredProducts: Product[];
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product) => void;
  onShowCustomProduct: () => void;
}

const ProductList = ({ 
  loading, 
  error,
  filteredProducts, 
  selectedProduct, 
  setSelectedProduct,
  onShowCustomProduct 
}: ProductListProps) => {
  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin mb-2" />
        <p>Chargement des produits...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-destructive">
        <AlertTriangle className="h-8 w-8 mb-2" />
        <p className="mb-3">{error}</p>
        <Button 
          variant="outline" 
          className="mx-auto"
          onClick={onShowCustomProduct}
        >
          Ajouter un produit personnalisé
        </Button>
      </div>
    );
  }
  
  if (filteredProducts.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <p className="mb-3">Aucun produit trouvé</p>
        <Button 
          variant="outline" 
          className="mx-auto"
          onClick={onShowCustomProduct}
        >
          Ajouter un produit personnalisé
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filteredProducts.map((product) => {
          // Use discounted price if available and not zero
          const displayPrice = product.discount_price && product.discount_price > 0 
            ? product.discount_price 
            : product.price;
            
          return (
            <Card 
              key={product.id}
              className={`cursor-pointer transition-all border-2 hover:shadow-md ${
                selectedProduct?.id === product.id ? 'border-primary' : 'border-transparent'
              }`}
              onClick={() => setSelectedProduct(product)}
            >
              <CardContent className="p-4 flex flex-col gap-2">
                <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-2">
                  {product.media_urls && product.media_urls.length > 0 ? (
                    <img 
                      src={product.media_urls[0]} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                      <Image className="h-12 w-12 opacity-40" />
                    </div>
                  )}
                </div>
                
                <h4 className="font-medium text-base line-clamp-2">{product.name}</h4>
                
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-medium text-primary">
                    {typeof displayPrice === 'number' 
                      ? new Intl.NumberFormat('fr-FR', { 
                          style: 'currency', 
                          currency: product.currency || 'DZD' 
                        }).format(displayPrice)
                      : 'Prix non défini'}
                  </span>
                  
                  {selectedProduct?.id === product.id && (
                    <div className="h-3 w-3 rounded-full bg-primary"></div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <Button 
        variant="link" 
        className="w-full text-center mt-4"
        onClick={onShowCustomProduct}
      >
        Ajouter un produit personnalisé
      </Button>
    </div>
  );
};

export default ProductList;
